import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SelectMenu, TagsInput } from '@jordan/ui';
import { TIMEZONES } from '../openslot/helpers';
import { Turnstile } from './Turnstile';
import './booking.css';

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

interface Colors {
  accent: string; accentText: string;
  selected: string; selectedText: string;
  available: string; availableText: string;
  timeSlot: string; timeSlotText: string;
  base: string; baseText: string;
  dash: string;
}
interface EventInfo {
  id: string; title: string; description: string | null;
  durationMin: number; durations: number[];
  locationType: string; locationDetail: string | null;
  ownerName: string; imageUrl: string | null; turnstileSiteKey: string | null; colors: Colors;
}
interface SlotsData { durationMin: number; durations: number[]; days: { date: string; slots: string[] }[] }

const LOC: Record<string, string> = {
  GOOGLE_MEET: 'Google Meet', ZOOM: 'Zoom', PHONE: 'Phone', IN_PERSON: 'In person', CUSTOM: 'Custom',
};
const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function BookingPage() {
  const { slug } = useParams();
  const [info, setInfo] = useState<EventInfo | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [slots, setSlots] = useState<SlotsData | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [zone, setZone] = useState(detectedTz);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [day, setDay] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [form, setForm] = useState({ inviteeName: '', inviteeNotes: '' });
  const [emails, setEmails] = useState<string[]>([]);
  const [done, setDone] = useState<{ meetingUrl: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Re-render the clock in the timezone selector each minute.
  const [, setTick] = useState(0);
  const [copyright, setCopyright] = useState<string | null>(null);
  const [tsToken, setTsToken] = useState<string | null>(null);
  const [tsReset, setTsReset] = useState(0);
  // Custom time-slot scrollbar: a real thumb shown only when the slots overflow.
  const slotsRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ top: number; height: number } | null>(null);

  const updateThumb = useCallback(() => {
    const el = slotsRef.current;
    if (!el || el.scrollHeight <= el.clientHeight + 1) {
      setThumb(null);
      return;
    }
    const track = el.clientHeight;
    const height = Math.max(28, (track * track) / el.scrollHeight);
    const max = track - height;
    const top = max <= 0 ? 0 : (el.scrollTop / (el.scrollHeight - el.clientHeight)) * max;
    setThumb({ top, height });
  }, []);

  useEffect(() => {
    fetch(`/api/public/event-type?slug=${encodeURIComponent(slug ?? '')}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d: EventInfo) => { setInfo(d); setDuration(d.durationMin); })
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!info || duration == null) return;
    setSlots(null);
    fetch(`/api/public/slots?slug=${encodeURIComponent(slug ?? '')}&duration=${duration}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setSlots)
      .catch(() => setErr('Availability is temporarily unavailable.'));
  }, [info, duration, slug]);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    fetch('/api/config/public')
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((cfg: { copyright?: string }) => setCopyright(cfg.copyright ?? null))
      .catch(() => setCopyright(null));
  }, []);

  const zones = useMemo(() => Array.from(new Set([detectedTz, ...TIMEZONES])), []);
  const availableDates = useMemo(() => new Set(slots?.days.map((d) => d.date) ?? []), [slots]);
  const daySlots = useMemo(() => slots?.days.find((d) => d.date === day)?.slots ?? [], [slots, day]);

  // Recompute the scrollbar thumb when the slots, day, or timezone change (and on resize).
  useLayoutEffect(() => {
    updateThumb();
    window.addEventListener('resize', updateThumb);
    return () => window.removeEventListener('resize', updateThumb);
  }, [daySlots, day, zone, updateThumb]);

  if (notFound) {
    return <Centered><h4>Booking link not found</h4><p className="text-muted">This link may be inactive.</p></Centered>;
  }
  if (!info) return <Centered><div className="spinner-border text-primary" role="status" /></Centered>;

  const c = info.colors;
  const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: zone });
  const nowInZone = new Date().toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: zone });

  const book = async () => {
    if (!slot) return;
    setBusy(true); setErr(null);
    try {
      const res = await fetch('/api/public/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventTypeId: info.id, start: slot, durationMin: duration, timezone: zone, turnstileToken: tsToken ?? undefined, inviteeEmails: emails, ...form }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Booking failed');
      setDone({ meetingUrl: data.meetingUrl ?? null });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Booking failed');
      // The verification token is single-use; refresh the widget for a retry.
      setTsToken(null);
      setTsReset((n) => n + 1);
    } finally {
      setBusy(false);
    }
  };

  // Month grid (weeks of Date|null)
  const startPad = new Date(month.getFullYear(), month.getMonth(), 1).getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = [
    ...Array(startPad).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1)),
  ];
  const today = ymd(new Date());

  const onThumbDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = slotsRef.current;
    if (!el || !thumb) return;
    const startY = e.clientY;
    const startTop = el.scrollTop;
    const max = el.clientHeight - thumb.height;
    const move = (ev: MouseEvent) => {
      if (max <= 0) return;
      el.scrollTop = startTop + ((ev.clientY - startY) / max) * (el.scrollHeight - el.clientHeight);
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  return (
    <div className="min-vh-100 py-4 py-md-5" style={{ background: '#f5f6fa' }}>
      <div className="container" style={{ maxWidth: 960 }}>
        <div className="row g-3 justify-content-center">
          {/* Left: event info */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-body p-4">
                {info.imageUrl ? (
                  <img src={info.imageUrl} alt="" className="mb-3" style={{ maxHeight: 56, maxWidth: 160, objectFit: 'contain' }} />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 mb-3"
                    style={{ width: 56, height: 56, background: c.dash, color: c.accentText }}
                  >
                    <i className="ph-calendar-check" style={{ fontSize: 28 }} />
                  </div>
                )}
                <div className="text-muted small mb-1">{info.ownerName}</div>
                <h4 className="fw-bold mb-3">{info.title}</h4>
                <div className="d-flex align-items-center text-muted mb-2"><i className="ph-clock me-2" />{duration} min</div>
                <div className="d-flex align-items-center text-muted mb-2">
                  <i className="ph-map-pin me-2" />{LOC[info.locationType] ?? info.locationType}
                  {info.locationDetail ? ` — ${info.locationDetail}` : ''}
                </div>
                {info.description && <p className="text-muted mt-3 mb-0">{info.description}</p>}
                {info.durations.length > 1 && !slot && !done && (
                  <div className="mt-4">
                    <label className="form-label small text-muted mb-1">Duration</label>
                    <SelectMenu
                      icon="ph-clock"
                      className="mb-0"
                      value={String(duration ?? '')}
                      onChange={(v) => { setDuration(Number(v)); setDay(null); setSlot(null); }}
                      options={info.durations.map((d) => ({ value: String(d), label: `${d} min` }))}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: calendar / slots / form / done */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-body p-4">
                {err && <div className="alert alert-warning">{err}</div>}

                {done ? (
                  <div className="text-center py-5">
                    <i className="ph-check-circle" style={{ fontSize: 56, color: c.accent }} />
                    <h4 className="mt-3 fw-bold">You&rsquo;re booked!</h4>
                    <p className="text-muted">A calendar invite is on its way to {emails.join(', ')}.</p>
                    {done.meetingUrl && (
                      <a className="btn rounded-3" style={{ background: c.accent, color: c.accentText }} href={done.meetingUrl} target="_blank" rel="noreferrer">
                        Join meeting
                      </a>
                    )}
                  </div>
                ) : slot ? (
                  <div style={{ maxWidth: 460 }}>
                    <button className="btn btn-link px-0 mb-2 text-decoration-none" onClick={() => setSlot(null)}>
                      <i className="ph-arrow-left me-1" />Back
                    </button>
                    <h5 className="fw-bold mb-1">Enter your details</h5>
                    <div className="text-muted mb-3">
                      {new Date(slot).toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short', timeZone: zone })}
                    </div>
                    <div className="mb-2"><label className="form-label">Name</label><input className="form-control rounded-3" value={form.inviteeName} onChange={(e) => setForm({ ...form, inviteeName: e.target.value })} /></div>
                    <div className="mb-2">
                      <label className="form-label">Email</label>
                      <TagsInput
                        inputType="email"
                        validate={isEmail}
                        separators={[',', ' ']}
                        placeholder="Type an email and press Enter — add more than one"
                        hint="Add one or more email addresses. Each valid address becomes a tag automatically."
                        onChange={setEmails}
                      />
                    </div>
                    <div className="mb-3"><label className="form-label">Notes (optional)</label><textarea className="form-control rounded-3" rows={2} value={form.inviteeNotes} onChange={(e) => setForm({ ...form, inviteeNotes: e.target.value })} /></div>
                    {info.turnstileSiteKey && (
                      <Turnstile siteKey={info.turnstileSiteKey} onToken={setTsToken} resetKey={tsReset} />
                    )}
                    <button
                      className="btn w-100 rounded-3 py-2"
                      style={{ background: c.accent, color: c.accentText }}
                      disabled={busy || !form.inviteeName || emails.length === 0 || (!!info.turnstileSiteKey && !tsToken)}
                      onClick={book}
                    >
                      {busy ? 'Booking…' : 'Confirm booking'}
                    </button>
                  </div>
                ) : (
                  <>
                    <h5 className="fw-bold mb-4">Select a time</h5>
                    <div className="row g-4">
                      {/* Calendar + timezone */}
                      <div className="col-md-7">
                        <div className="d-flex align-items-center mb-3">
                          <h6 className="fw-bold mb-0 me-auto">{month.toLocaleString(undefined, { month: 'long', year: 'numeric' })}</h6>
                          <button className="btn btn-sm btn-light btn-icon rounded-circle" aria-label="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>
                            <i className="ph-caret-left" />
                          </button>
                          <button className="btn btn-sm btn-light btn-icon rounded-circle ms-1" aria-label="Next month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>
                            <i className="ph-caret-right" />
                          </button>
                        </div>
                        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7,1fr)', gap: 6, rowGap: 4 }}>
                          {WEEKDAYS.map((d) => <div key={d} className="text-center text-muted small fw-semibold pb-1">{d}</div>)}
                          {cells.map((d, i) => {
                            if (!d) return <div key={i} />;
                            const k = ymd(d);
                            const avail = availableDates.has(k) && k >= today;
                            const sel = k === day;
                            const isToday = k === today;
                            return (
                              <button
                                key={i}
                                type="button"
                                disabled={!avail}
                                onClick={() => setDay(k)}
                                className="os-day"
                                style={{
                                  background: sel ? c.selected : avail ? c.available : 'transparent',
                                  color: sel ? c.selectedText : avail ? c.availableText : '#cbd5e1',
                                  border: !sel && isToday ? '1px solid #cbd5e1' : 'none',
                                  fontWeight: sel || avail ? 600 : 400,
                                }}
                              >
                                {d.getDate()}
                              </button>
                            );
                          })}
                        </div>

                        <div className="mt-4">
                          <label className="form-label small text-muted mb-1">
                            Time zone <span className="ms-1">· {nowInZone}</span>
                          </label>
                          <SelectMenu
                            searchable
                            icon="ph-globe"
                            className="mb-0"
                            value={zone}
                            onChange={setZone}
                            options={zones.map((z) => ({ value: z, label: z }))}
                          />
                        </div>
                      </div>

                      {/* Time slots */}
                      <div className="col-md-5">
                        {!day ? (
                          <div className="text-muted">{slots ? 'Select a date.' : 'Loading…'}</div>
                        ) : (
                          <>
                            <div className="fw-bold">
                              {new Date(`${day}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                            </div>
                            <div className="text-muted small mb-3">Times in {zone} · {duration} min</div>
                            <div className="os-slots-wrap">
                              <div ref={slotsRef} className="os-timeslots" onScroll={updateThumb}>
                                {daySlots.length === 0 && <div className="text-muted">No times.</div>}
                                {daySlots.map((s) => (
                                  <button
                                    key={s}
                                    type="button"
                                    className="btn w-100 mb-2 rounded-3 py-2 fw-semibold"
                                    style={{ border: `1px solid ${c.timeSlot}`, color: c.timeSlotText, background: 'transparent' }}
                                    onClick={() => setSlot(s)}
                                  >
                                    {fmtTime(s)}
                                  </button>
                                ))}
                              </div>
                              {thumb && (
                                <div
                                  className="os-scrollthumb"
                                  style={{ top: thumb.top, height: thumb.height }}
                                  onMouseDown={onThumbDrag}
                                />
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        {copyright && (
          <div className="text-center text-muted small mt-3">
            {copyright.replace('{year}', String(new Date().getFullYear()))}
          </div>
        )}
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="d-flex vh-100 align-items-center justify-content-center text-center"><div>{children}</div></div>;
}
